/**
 * ItemVisibilitySection
 *
 * Read-only panel showing who can see a prioritization item.
 * Access is derived from workspace membership and cycle/track/session participation —
 * not per-item ACLs. Visual pattern echoes cycle inheritance UIs but focuses on
 * audience, not inherited settings.
 */

import { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { ChevronDown, ChevronRight, Eye, Users, Building2, RefreshCw, ExternalLink } from 'lucide-react';
import { useItemVisibility, type ItemVisibilityData } from '@/hooks/prioritization/useItemVisibility';
import { useActorDisplayMap } from '@/hooks/prioritization/useActorDisplayMap';
import { getItemCountState } from '@/components/collection/types';

interface Props {
  itemId: string;
}

function SourceBadge({ label, tone = 'neutral' }: { label: string; tone?: 'neutral' | 'inherited' | 'session' }) {
  const cls =
    tone === 'inherited'
      ? 'bg-[var(--hover-tint)] text-[var(--text-sec)] dark:bg-[var(--hover-tint)] dark:text-[var(--text-sec)]'
      : tone === 'session'
        ? 'bg-[var(--slate-dim)] text-[var(--text-sec)] dark:bg-[var(--slate)]/50'
        : 'bg-[var(--hover-tint)] text-[var(--text-sec)] dark:bg-[var(--hover-tint)] dark:text-[var(--text-sec)]';
  return <span className={`px-2 py-0.5 rounded text-xs font-medium ${cls}`}>{label}</span>;
}

function PeopleList({
  people,
  displayMap,
}: {
  people: Array<{ userId: string; role?: string }>;
  displayMap: Record<string, string>;
}) {
  const [query, setQuery] = useState('');
  const countState = getItemCountState(people.length);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return people;
    return people.filter((p) => {
      const name = (displayMap[p.userId] ?? p.userId).toLowerCase();
      return name.includes(q);
    });
  }, [people, displayMap, query]);

  if (people.length === 0) {
    return <p className="text-sm text-[var(--text-ter)] italic">None</p>;
  }

  return (
    <div className="space-y-2">
      {countState === 'many' && (
        <input
          type="search"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          placeholder="Search people…"
          className="w-full text-sm px-2.5 py-1.5 rounded border border-[var(--rule)] dark:border-[var(--rule)] bg-[var(--bg-raised)] dark:bg-[var(--surface)] focus:outline-none focus:ring-1 focus:ring-primary-400"
        />
      )}
      <ul className="divide-y divide-[var(--rule)] dark:divide-[var(--rule)] border border-[var(--rule)] dark:border-[var(--rule)] overflow-hidden bg-[var(--bg-raised)] dark:bg-[var(--surface)]">
        {filtered.map((p) => (
          <li key={p.userId} className="flex items-center justify-between gap-3 px-3 py-2 text-sm">
            <span className="font-medium text-[var(--text-pri)] dark:text-[var(--text-pri)] truncate">
              {displayMap[p.userId] ?? p.userId}
            </span>
            {p.role && (
              <span className="text-xs text-[var(--text-ter)] dark:text-[var(--text-ter)] flex-shrink-0">{p.role}</span>
            )}
          </li>
        ))}
        {filtered.length === 0 && (
          <li className="px-3 py-2 text-sm text-[var(--text-ter)] italic">No matches</li>
        )}
      </ul>
    </div>
  );
}

function GroupList({ groups }: { groups: Array<{ groupId: string; groupName: string }> }) {
  if (groups.length === 0) return null;
  return (
    <ul className="flex flex-wrap gap-1.5 mt-2">
      {groups.map((g) => (
        <li
          key={g.groupId}
          className="inline-flex items-center gap-1 px-2 py-0.5 rounded border border-[var(--rule)] dark:border-[var(--rule)] bg-[var(--surface)] dark:bg-[var(--hover-tint)] text-xs text-[var(--text-sec)] dark:text-[var(--text-sec)]"
        >
          <Users className="h-3 w-3 opacity-60" />
          {g.groupName}
        </li>
      ))}
    </ul>
  );
}

function ItemVisibilityDetails({ visibility }: { visibility: ItemVisibilityData }) {
  const peopleIdsKey = useMemo(() => {
    const ids = new Set<string>(visibility.allViewerIds);
    for (const cycle of visibility.cycleContexts) {
      for (const p of cycle.cyclePeople) ids.add(p.userId);
      for (const track of cycle.tracks) {
        for (const p of track.trackOnlyPeople) ids.add(p.userId);
      }
    }
    for (const session of visibility.sessionContexts) {
      for (const p of session.people) ids.add(p.userId);
    }
    return [...ids].sort().join(',');
  }, [
    visibility.allViewerIds,
    visibility.cycleContexts,
    visibility.sessionContexts,
  ]);

  const peopleIds = useMemo(
    () => (peopleIdsKey ? peopleIdsKey.split(',') : []),
    [peopleIdsKey],
  );

  const displayMap = useActorDisplayMap(peopleIds);

  const hasCycleContext = visibility.cycleContexts.length > 0;
  const hasSessionContext = visibility.sessionContexts.length > 0;

  if (visibility.loading) {
    return (
      <div className="px-4 pb-4 pt-3 border-t border-[var(--rule)] dark:border-[var(--rule)] flex items-center gap-2 text-sm text-[var(--text-ter)]">
        <RefreshCw className="h-3.5 w-3.5 animate-spin" />
        Loading access details…
      </div>
    );
  }

  return (
    <div className="px-4 pb-4 space-y-4 border-t border-[var(--rule)] dark:border-[var(--rule)] pt-3">
      <p className="text-xs text-[var(--text-ter)] dark:text-[var(--text-sec)]">
        Item <span className="font-medium text-[var(--text-sec)] dark:text-[var(--text-pri)]">read access</span> comes from
        workspace membership and participation in cycles, tracks, or isolated sessions — not route visibility, edit
        permissions, or a per-item setting. Group members are listed by group name.
      </p>

      <section>
        <div className="flex items-center gap-2 mb-2">
          <Building2 className="h-3.5 w-3.5 text-[var(--text-ter)]" />
          <h4 className="text-sm font-medium text-[var(--text-pri)] dark:text-[var(--text-pri)]">Workspace</h4>
          <SourceBadge label="All members" />
        </div>
        <p className="text-xs text-[var(--text-ter)] dark:text-[var(--text-sec)] mb-2">
          Everyone in this workspace can view items here.
          {visibility.workspaceMemberCount > 0 && (
            <> ({visibility.workspaceMemberCount} {visibility.workspaceMemberCount === 1 ? 'member' : 'members'})</>
          )}
        </p>
      </section>

      {hasCycleContext &&
        visibility.cycleContexts.map((cycle) => (
          <section key={cycle.cycleId} className="space-y-3">
            <div className="flex items-center gap-2 flex-wrap">
              <h4 className="text-sm font-medium text-[var(--text-pri)] dark:text-[var(--text-pri)]">{cycle.cycleName}</h4>
              <SourceBadge label="Cycle participation" tone="inherited" />
              <Link
                to={`/prioritization/cycles/${cycle.cycleId}`}
                className="inline-flex items-center gap-1 text-xs text-primary-600 hover:text-primary-700 dark:text-primary-400"
              >
                Manage participants
                <ExternalLink className="h-3 w-3" />
              </Link>
            </div>

            <div>
              <p className="text-xs font-medium text-[var(--text-sec)] dark:text-[var(--text-sec)] mb-1.5">
                From cycle (applies to all tracks)
              </p>
              <PeopleList people={cycle.cyclePeople} displayMap={displayMap} />
              <GroupList groups={cycle.cycleGroups} />
            </div>

            {cycle.tracks.map((track) => {
              const hasTrackOnly = track.trackOnlyPeople.length > 0 || track.trackOnlyGroups.length > 0;
              if (!hasTrackOnly) return null;
              return (
                <div key={track.trackId}>
                  <p className="text-xs font-medium text-[var(--text-sec)] dark:text-[var(--text-sec)] mb-1.5 flex items-center gap-2 flex-wrap">
                    <span>
                      {track.trackName}
                      <span className="font-normal text-[var(--text-ter)] dark:text-[var(--text-ter)]">
                        {' '}
                        — track-only participants
                      </span>
                    </span>
                    <Link
                      to={`/prioritization/tracks/${track.trackId}`}
                      className="inline-flex items-center gap-1 text-primary-600 hover:text-primary-700 dark:text-primary-400 font-normal"
                    >
                      Manage
                      <ExternalLink className="h-3 w-3" />
                    </Link>
                  </p>
                  <PeopleList people={track.trackOnlyPeople} displayMap={displayMap} />
                  <GroupList groups={track.trackOnlyGroups} />
                </div>
              );
            })}
          </section>
        ))}

      {!hasCycleContext && (
        <p className="text-sm text-[var(--text-ter)] italic">
          This item is not in a cycle yet — visibility is workspace-wide only.
        </p>
      )}

      {hasSessionContext &&
        visibility.sessionContexts.map((session) => (
          <section key={session.sessionId}>
            <div className="flex items-center gap-2 mb-2">
              <h4 className="text-sm font-medium text-[var(--text-pri)] dark:text-[var(--text-pri)]">{session.sessionName}</h4>
              <SourceBadge label="Isolated session" tone="session" />
            </div>
            <p className="text-xs text-[var(--text-ter)] dark:text-[var(--text-sec)] mb-2">
              Session participants can view items isolated to this session.
            </p>
            <PeopleList people={session.people} displayMap={displayMap} />
            <GroupList groups={session.groups} />
          </section>
        ))}

      {visibility.externalViewerCount > 0 && (
        <p className="text-xs text-[var(--mustard)] dark:text-[var(--mustard)] bg-[var(--mustard-dim)] dark:bg-[var(--mustard)]/30 border border-[var(--mustard)] dark:border-[var(--mustard)]/50 px-3 py-2">
          {visibility.externalViewerCount}{' '}
          {visibility.externalViewerCount === 1 ? 'person' : 'people'} outside the workspace also have access
          via cycle, track, or session participation.
        </p>
      )}
    </div>
  );
}

export function ItemVisibilitySection({ itemId }: Props) {
  const [expanded, setExpanded] = useState(false);
  const visibility = useItemVisibility(itemId);

  const hasCycleContext = visibility.cycleContexts.length > 0;
  const summaryParts: string[] = [];
  if (visibility.workspaceMemberCount > 0) {
    summaryParts.push(
      visibility.workspaceMemberCount === 1
        ? '1 workspace member'
        : `${visibility.workspaceMemberCount} workspace members`,
    );
  }
  if (hasCycleContext) {
    summaryParts.push(
      visibility.cycleContexts.length === 1 ? '1 cycle' : `${visibility.cycleContexts.length} cycles`,
    );
  }
  if (visibility.externalViewerCount > 0) {
    summaryParts.push(
      visibility.externalViewerCount === 1
        ? '1 external participant'
        : `${visibility.externalViewerCount} external participants`,
    );
  }

  return (
    <div className="border border-[var(--rule)] dark:border-[var(--rule)] bg-[var(--surface)]/50 dark:bg-[var(--surface)]">
      <button
        type="button"
        onClick={() => setExpanded((v) => !v)}
        className="w-full flex items-start gap-3 px-4 py-3 text-left hover:bg-[var(--surface)]/80 dark:hover:bg-[var(--hover-tint)] transition-colors"
        aria-expanded={expanded}
      >
        <Eye className="h-4 w-4 text-[var(--text-ter)] dark:text-[var(--text-ter)] mt-0.5 flex-shrink-0" />
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <span className="text-xs font-semibold uppercase tracking-wide text-[var(--text-ter)] dark:text-[var(--text-ter)]">
              Read access
            </span>
            {visibility.loading && (
              <RefreshCw className="h-3 w-3 animate-spin text-[var(--text-ter)]" aria-label="Loading visibility" />
            )}
          </div>
          <p className="text-sm text-[var(--text-pri)] dark:text-[var(--text-pri)] mt-0.5">
            {visibility.loading
              ? 'Loading access…'
              : visibility.totalViewerCount === 0
                ? 'No viewers resolved yet'
                : `${visibility.totalViewerCount} ${visibility.totalViewerCount === 1 ? 'person' : 'people'} can view this item`}
          </p>
          {!visibility.loading && (
            <p className="text-xs text-[var(--text-ter)] dark:text-[var(--text-sec)] mt-0.5">
              Membership-based read access — not edit permissions or navigation
            </p>
          )}
          {!visibility.loading && summaryParts.length > 0 && (
            <p className="text-xs text-[var(--text-ter)] dark:text-[var(--text-sec)] mt-0.5">
              via {summaryParts.join(' · ')}
            </p>
          )}
        </div>
        {expanded ? (
          <ChevronDown className="h-4 w-4 text-[var(--text-ter)] flex-shrink-0 mt-0.5" />
        ) : (
          <ChevronRight className="h-4 w-4 text-[var(--text-ter)] flex-shrink-0 mt-0.5" />
        )}
      </button>

      {expanded && <ItemVisibilityDetails visibility={visibility} />}
    </div>
  );
}

export default ItemVisibilitySection;
